import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Button, Tag, Card, Input, Row, Col, Popconfirm, message, Popover } from "antd";
import { format } from "rut.js";
import { handleError } from "../../../utils/errorHandler";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { getCartransfers, removeCartransfer } from "../../../api/cartransferService";

const fullName = (p) =>
  `${p?.name || ""} ${p?.firstLastName || ""} ${p?.secondLastName || ""}`.trim() || "-";

const PersonCell = ({ record, role }) => {
  const list =
    role === "seller"
      ? record.sellers?.length > 0 ? record.sellers : record.seller ? [record.seller] : []
      : record.buyers?.length > 0 ? record.buyers : record.buyer ? [record.buyer] : [];

  if (list.length === 0) return "-";

  const primary = fullName(list[0]);

  if (list.length === 1) return <span>{primary}</span>;

  const content = (
    <div style={{ minWidth: 220 }}>
      {list.map((p, i) => (
        <div key={i} style={{ marginBottom: i < list.length - 1 ? 10 : 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{fullName(p)}</div>
          {p.rut && (
            <div style={{ fontSize: 12, color: "#6b7280" }}>CI: {format(p.rut)}</div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <div>{primary}</div>
      <Popover
        content={content}
        title={`${role === "seller" ? "Vendedores" : "Compradores"} (${list.length})`}
        trigger="click"
        placement="right"
      >
        <Tag
          color="blue"
          style={{ cursor: "pointer", marginTop: 4, fontSize: 11, userSelect: "none" }}
        >
          +{list.length - 1} más
        </Tag>
      </Popover>
    </div>
  );
};

const CarTranserList = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const [loading, setLoading] = useState(false);
  const [cartransfers, setCartransfers] = useState([]);
  const [total, setTotal] = useState(0);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await getCartransfers(pagination.pageSize, pagination.current, undefined, "repertory:desc", q || undefined);
      setCartransfers(res.data.results || []);
      setTotal(res.data.totalResults || 0);
    } catch (error) {
      handleError(error, message.error, "Error al cargar transferencias", "fetchList");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, q]);
  
  // Función para manejar la eliminación
  const handleDelete = async (id, repertory) => {
    try {
      await removeCartransfer(id);
      message.success(`Transferencia ${repertory} eliminada exitosamente`);
      fetchList(); // Recargar la lista
    } catch (error) {
      handleError(error, message.error, "Error al eliminar la transferencia", "handleDelete");
    }
  };
  

  

  


  const columns = [
    {
      title: "R",
      dataIndex: "repertory",
      key: "repertory",
      width: 60,
    },
    {
      title: "Ano",
      dataIndex: "repertoryYear",
      key: "repertoryYear",
      width: 80,
    },
    {
      title: "Vendedor",
      key: "seller",
      render: (_, record) => <PersonCell record={record} role="seller" />,
      width: 200,
    },
    {
      title: "Comprador",
      key: "buyer",
      render: (_, record) => <PersonCell record={record} role="buyer" />,
      width: 200,
    },
    {
      title: "Tipo",
      key: "type",
      width: 120,
      render: (_, record) => {
        const type = record.contract?.type || record.background?.type;
        return type || "-";
      },
    },
    {
      title: "Ano",
      key: "year",
      width: 80,
      render: (_, record) => {
        const year = record.contract?.year || record.background?.year;
        return year || "-";
      },
    },
    {
      title: "Marca",
      key: "make",
      width: 120,
      render: (_, record) => {
        const make = record.contract?.make || record.background?.make;
        return make || "-";
      },
    },
    {
      title: "Motor",
      key: "engine",
      width: 80,
      render: (_, record) => {
        const motor = record.contract?.motor || record.background?.motor;
        return motor || "-";
      },
    },
    {
      title: "Patente",
      key: "plate",
      width: 100,
      render: (_, record) => {
        const inscription = record.contract?.inscription || record.background?.inscription;
        return inscription || "-";
      },
    },
    {
      title: "Impuesto",
      dataIndex: ["contract", "tax"],
      key: "tax",
      width: 100,
      render: (tax) => tax ? `$${tax.toLocaleString()}` : "-",
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Tag color={status === "cerrado" ? "green" : status === "creado" ? "blue" : "orange"}>
          {status === "cerrado" ? "Cerrado" : status === "creado" ? "Creado" : status === "borrador" ? "Borrador" : status}
        </Tag>
      ),
    },
    {
      title: "Accion",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/admin/cartransfers/edit/${record.id}`)}
            size="small"
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar esta transferencia?"
            description={`Se eliminará la transferencia ${record.repertory}-${record.repertoryYear}`}
            onConfirm={() => handleDelete(record.id, `${record.repertory}-${record.repertoryYear}`)}
            okText="Sí, eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
              // sin estado de carga por elemento
            >
              Eliminar
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  // Errores ya se reportan en fetchList mediante handleError

  return (
    <Card
      title="Transferencias de Vehículos"
      style={{ margin: 10 }}
    >
      <Row style={{ marginBottom: 16 }}>
        <Col span={16}>
          <Input
            placeholder="Buscar por datos repertorio, vehículo, personas..."
            allowClear
            onChange={(e) => setQ(e.target.value)}
            value={q}
            style={{ marginBottom: 16 }}
          />
        </Col>
        <Col span={8} style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            onClick={() => navigate('/admin/cartransfers/new')}
            style={{ marginBottom: 16 }}
          >
            Crear
          </Button>
        </Col>
      </Row>
      <Table
        columns={columns}
        dataSource={cartransfers}
        loading={loading}
        rowKey="id"
        pagination={{
          ...pagination,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} cartransfers`,
        }}
        onChange={handleTableChange}
      />
    </Card>
  );
};

export default CarTranserList;
